/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { observable, reaction } from 'mobx';
import * as React from 'react';

import { INosCompanytypePickerData } from './nos-companytype-picker.data';
import { INosCompanytypePickerProps } from './nos-companytype-picker.props.autogenerated';
import BusinessSignUpDropdown from './components/sign-up-dropdown';
import { IGetAvailableCompanyTypeData } from '../../actions/get-available-company-types.action';

/**
 * Interface for ICompanyTypesExtendedProps.
 */
export interface ICompanyTypePickerExtendedProps extends INosCompanytypePickerProps<INosCompanytypePickerData> {
    updateDropdown(newSelectValue: IGetAvailableCompanyTypeData): void;
    selectedValue: IGetAvailableCompanyTypeData;
    isSubmittedPressed: boolean;
}

/**
 * Interface for ICompanyTypesState.
 */
export interface ICompanyTypePickerState {
    companyTypeId?: string;
}

/**
 * Interface for ICompanyTypesViewProps.
 */
export interface ICompanyTypePickerViewProps {
    className: string;
    CompanyTypePicker: IModuleProps;
    availableCompanyTypes: any;
}

/**
 *
 * Address component.
 * @extends {React.Component<INosCompanyTypePickerProps<INosCompanyTypePickerData>, ICompanyTypePickerState>}
 */
class NosCompanyTypePicker extends React.Component<ICompanyTypePickerExtendedProps, ICompanyTypePickerState> {
    @observable private availableCompanyTypes?: IGetAvailableCompanyTypeData[];

    public constructor(props: ICompanyTypePickerExtendedProps) {
        super(props);

        const { data } = this.props;

        this.availableCompanyTypes = data.availableCompanyTypes.result || [];
    }

    public componentDidMount(): void {
        // Initializing data props
        this._dataInitialize(this.props);
    }

    public shouldComponentUpdate(nextProps: ICompanyTypePickerExtendedProps, nextState: ICompanyTypePickerState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        const { renderView, config, selectedValue } = this.props;

        const viewProps = {
            ...this.props,
            className: config.className,
            CompanyTypePicker: {
                moduleProps: this.props,
                className: classnames('ms-business-account-address', config.className)
            },
            availableCompanyTypes: this._initializeDropdown(this.availableCompanyTypes ?? [], selectedValue)
        };

        return renderView(viewProps) as React.ReactElement;
    }

    private readonly _initializeDropdown = (
        availableCompanyTypes: IGetAvailableCompanyTypeData[],
        selectedValue: IGetAvailableCompanyTypeData
    ) => {
        const tempOptions = availableCompanyTypes?.map(el => {
            return {
                key: el.key,
                value: el.value
            };
        });

        return (
            <BusinessSignUpDropdown
                className={''}
                isRequired={false}
                options={tempOptions}
                isSubmittedPressed={this.props.isSubmittedPressed}
                onChange={(event: any) => this._onInputChange(event, availableCompanyTypes)}
                selectValue={selectedValue}
                value={selectedValue?.value ?? ''}
                resources={this.props.resources}
            />
        );
    };

    private readonly _onInputChange = (
        event: React.ChangeEvent<HTMLSelectElement>,
        availableCompanyTypes: IGetAvailableCompanyTypeData[]
    ) => {
        const newChosenLanguage = availableCompanyTypes.find(el => el.value === event.target.value);
        if (newChosenLanguage) {
            this.props.updateDropdown(newChosenLanguage);
        }
    };

    /**
     * Method data initialization.
     * @param props -The account management props.
     */
    private readonly _dataInitialize = (props: ICompanyTypePickerExtendedProps): void => {
        const { data } = props;

        reaction(
            () => data.availableCompanyTypes.result,
            () => {
                this.availableCompanyTypes = data.availableCompanyTypes.result ?? [];
            }
        );
    };
}

export default NosCompanyTypePicker;
